import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  LinearProgress,
  Fab,
  Tooltip,
} from '@mui/material';
import CountUp from 'react-countup';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Cell,
} from 'recharts';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import DinnerDiningIcon from '@mui/icons-material/DinnerDining';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import KitchenIcon from '@mui/icons-material/Kitchen';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';

// ── Static mock data ──
const Companycurrency = '₹';
const userName = 'Admin';

const MOCK = {
  branchCount: 0,
  tableCount: 0,
  addRevenue: 0,
  netCashBalance: 0,
  totalDineIn: 0,
  totalTakeaway: 0,
  availableTableQty: 0,
  occupiedTableQty: 0,
  totalSalePayment: 0,
  todaySalePayment: 0,
  monthlysale: 0,
  reservationsToday: 0,
  todayDelivery: 0,
  monthlyDelivery: 0,
  avgRating: 0,
  kitchenPending: 0,
  todayOrders: 0,
  activeOrders: 0,
  pendingOrders: 0,
};

/* ── Design tokens (original from hotel dashboard) ── */
const T = {
  blue: '#1A56DB',
  blueL: '#3b82f6',
  green: '#10b981',
  amber: '#f59e0b',
  rose: '#f43f5e',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
  orange: '#f97316',
  pink: '#ec4899',
  border: 'rgba(148,163,184,0.18)',
  bg: '#F1F5F9',
  card: '#ffffff',
  textPri: '#0F172A',
  textSec: '#64748B',
  textMut: '#94A3B8',
};

const alpha = (hex, opacity) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

const shadow = {
  card: '0 1px 3px rgba(15,23,42,0.04), 0 4px 16px rgba(15,23,42,0.04)',
  hover: (color) =>
    `0 4px 12px rgba(15,23,42,0.06), 0 12px 32px ${alpha(color, 0.18)}`,
};

/* ── SVG Occupancy Ring ── */
function OccupancyRing({ pct, size = 88 }) {
  const r = size / 2 - 9;
  const circ = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const dash = (clamped / 100) * circ;
  const cx = size / 2, cy = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#ffffff" />
        </linearGradient>
      </defs>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth={7} />
      <circle
        cx={cx} cy={cy} r={r} fill="none" stroke="url(#ring-grad)" strokeWidth={7}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dasharray 1.2s ease' }}
      />
    </svg>
  );
}

/* ── Custom Chart Tooltip ── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ background: '#0F172A', borderRadius: '10px', p: '8px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
      <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.55)', mb: '2px' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
        {Companycurrency} {Number(payload[0]?.value || 0).toLocaleString()}
      </Typography>
    </Box>
  );
}

/* ── Section Label ── */
function SectionLabel({ label, color = T.blue, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '12px' }}>
      <Box sx={{ width: 3, height: 16, borderRadius: 2, background: `linear-gradient(180deg,${color},${alpha(color, 0.3)})` }} />
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: T.textSec, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      {action && <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>{action}</Box>}
    </Box>
  );
}

/* ── Revenue KPI Card ── */
function KpiCard({ title, value, color, icon, onClick, sub, grad }) {
  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: '16px', p: '16px 18px', height: '100%',
        background: T.card, border: `1px solid ${T.border}`, boxShadow: shadow.card,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: shadow.hover(color) } : {},
        display: 'flex', alignItems: 'flex-start', gap: '14px',
        overflow: 'hidden', minWidth: 0, position: 'relative',
      }}>
      <Box sx={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(135deg,${alpha(color, 0.04)} 0%,transparent 60%)`,
        borderRadius: '16px', pointerEvents: 'none',
      }} />
      <Box sx={{
        width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
        background: grad || `linear-gradient(135deg,${color},${alpha(color, 0.7)})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 4px 12px ${alpha(color, 0.35)}`,
        '& svg': { fontSize: 22, color: '#fff' },
      }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1, zIndex: 1 }}>
        <Typography sx={{
          fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.5px',
          textTransform: 'uppercase', color: T.textMut, lineHeight: 1, mb: '6px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{title}</Typography>
        <Typography sx={{
          fontSize: '1.15rem', fontWeight: 800, color: T.textPri, lineHeight: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.3px',
        }}>
          <span style={{ fontSize: '0.6em', color: T.textMut, marginRight: 2 }}>{Companycurrency}</span>
          <CountUp end={value} separator="," duration={1.3} />
        </Typography>
        {sub && <Typography sx={{ fontSize: '0.57rem', color: T.textMut, mt: '3px' }}>{sub}</Typography>}
      </Box>
    </Paper>
  );
}

/* ── Performance Metric Tile ── */
function MetricTile({ title, value, icon, color, onClick, isAmount }) {
  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: '14px', p: '14px 10px',
        // height:100% + flex column → every metric tile is the SAME height and
        // its content is vertically centred, so the grid rows align cleanly.
        height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        background: `linear-gradient(145deg,${alpha(color, 0.07)},${alpha(color, 0.02)})`,
        border: `1px solid ${alpha(color, 0.18)}`, boxShadow: shadow.card,
        transition: 'transform 0.2s, box-shadow 0.2s', textAlign: 'center',
        '&:hover': onClick ? { transform: 'translateY(-3px)', boxShadow: shadow.hover(color) } : {},
      }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '10px', mx: 'auto', mb: '10px',
        background: `linear-gradient(135deg,${color},${alpha(color, 0.65)})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 3px 10px ${alpha(color, 0.35)}`,
        '& svg': { fontSize: 18, color: '#fff' },
      }}>{icon}</Box>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color, lineHeight: 1, mb: '5px', letterSpacing: '-0.5px' }}>
        {isAmount && <span style={{ fontSize: '0.5em', color: alpha(color, 0.65), marginRight: 2 }}>{Companycurrency}</span>}
        <CountUp end={value} separator="," duration={1.3} />
      </Typography>
      <Typography sx={{ fontSize: '0.57rem', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: alpha(color, 0.65) }}>
        {title}
      </Typography>
    </Paper>
  );
}

/* ── Stat Mini Card ── */
function StatMini({ label, value, color, icon, pct, pctLabel }) {
  return (
    <Paper elevation={0} sx={{ borderRadius: '14px', p: '14px 16px', background: T.card, border: `1px solid ${T.border}`, boxShadow: shadow.card }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
        <Typography sx={{ fontSize: '0.57rem', fontWeight: 700, color: T.textSec, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          {label}
        </Typography>
        <Box sx={{ color, '& svg': { fontSize: 16 } }}>{icon}</Box>
      </Box>
      <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1, letterSpacing: '-0.4px', mb: '8px' }}>
        <CountUp end={value} separator="," duration={1.5} />
      </Typography>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: alpha(color, 0.12), overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${Math.min(pct, 100)}%`, bgcolor: color, borderRadius: 2, transition: 'width 1.2s ease' }} />
      </Box>
      {pctLabel && <Typography sx={{ mt: '4px', fontSize: '0.57rem', color: T.textMut }}>{pctLabel}</Typography>}
    </Paper>
  );
}

export default function RestaurantDashboard() {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [loading] = useState(false);
  const [data, setData] = useState(MOCK);
  // Top-selling items analytics (with Today / Week / Month filter).
  const [topItems, setTopItems] = useState([]);
  const [topPeriod, setTopPeriod] = useState('today');

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  // #1: Restaurant dashboard date filter (restaurant data has no hotel dim).
  const [rFrom, setRFrom] = useState('');
  const [rTo, setRTo] = useState('');
  const [rQs, setRQs] = useState('');
  const applyRFilter = () => {
    const qp = new URLSearchParams();
    if (rFrom) qp.set('from', rFrom);
    if (rTo) qp.set('to', rTo);
    const s = qp.toString();
    setRQs(s ? ('?' + s) : '');
  };
  // Live restaurant metrics (sales, tables, kitchen, cash) — same as the hotel
  // dashboard. Missing fields fall back to 0 via MOCK.
  useEffect(() => {
    const company = JSON.parse(
      sessionStorage.getItem('companyDetails') || '{}',
    );
    const token = sessionStorage.getItem('token');
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/restaurantorder/dashboard/${company.id}${rQs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d === 'object') setData({ ...MOCK, ...d });
      })
      .catch(() => {});
  }, [rQs]);

  // Top-selling items — refetches whenever the period filter changes.
  useEffect(() => {
    const company = JSON.parse(
      sessionStorage.getItem('companyDetails') || '{}',
    );
    const token = sessionStorage.getItem('token');
    if (!company?.id) return;
    fetch(
      `${config.API_BASE_URL}/restaurantorder/topitems/${company.id}?period=${topPeriod}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((r) => r.json())
      .then((d) => setTopItems(Array.isArray(d) ? d : []))
      .catch(() => setTopItems([]));
  }, [topPeriod]);

  // Today's reservation count for the occupancy card (Reserve Table module).
  useEffect(() => {
    const company = JSON.parse(
      sessionStorage.getItem('companyDetails') || '{}',
    );
    const token = sessionStorage.getItem('token');
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/reservation/stats/${company.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d === 'object')
          setData((prev) => ({ ...prev, reservationsToday: d.todayCount ?? 0 }));
      })
      .catch(() => {});
  }, []);

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return { text: 'Good Morning', emoji: '🌤️' };
    if (h < 17) return { text: 'Good Afternoon', emoji: '☀️' };
    return { text: 'Good Evening', emoji: '🌙' };
  };

  const greeting = getGreeting();

  const occPct = Math.min((data.occupiedTableQty / (data.tableCount || 1)) * 100, 100);
  const availablePct = (data.availableTableQty / (data.tableCount || 1)) * 100 || 0;

  const revenueData = [
    { name: 'Today Dine', amt: data.todaySalePayment, fill: T.blue },
    { name: 'Mthly Dine', amt: data.monthlysale, fill: T.blueL },
  ].filter((i) => i.amt > 0);

  const heroStats = [
    { label: "Today's Revenue", value: data.todaySalePayment, isAmt: true, color: '#60a5fa', icon: '💰' },
    { label: 'Monthly Revenue', value: data.monthlysale, isAmt: true, color: '#34d399', icon: '📈' },
    { label: 'Total Revenue', value: data.totalSalePayment, isAmt: true, color: '#fbbf24', icon: '🏆' },
    { label: 'Table Occupancy', value: occPct, suffix: '%', decimals: 1, color: '#c084fc', icon: '🍽️' },
  ];

  const kpiCards = [
    {
      title: 'Net Cash Balance', value: data.netCashBalance, color: T.blue,
      grad: `linear-gradient(135deg,${T.blue},${T.blueL})`,
      icon: <AccountBalanceWalletIcon />, sub: 'All time',
    },
    {
      title: "Today's Sales", value: data.todaySalePayment, color: T.green,
      grad: `linear-gradient(135deg,${T.green},#34d399)`,
      icon: <MonetizationOnIcon />,
      sub: now.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
    },
    {
      title: 'Monthly Sales', value: data.monthlysale, color: T.amber,
      grad: `linear-gradient(135deg,${T.amber},#fcd34d)`,
      icon: <TrendingUpIcon />,
      sub: now.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
    },
    {
      title: 'Total Revenue', value: data.totalSalePayment, color: T.rose,
      grad: `linear-gradient(135deg,${T.rose},#fb7185)`,
      icon: <AttachMoneyIcon />, sub: 'All time',
    },
  ];

  // All real / dynamic metrics. Removed the always-0 / non-applicable tiles
  // ("Takeaway Today", "Branches", "Today's/Monthly Delivery") and added live
  // order counts (Today's / Active / Pending orders).
  const perfTiles = [
    { title: "Today's Orders", value: data.todayOrders, icon: <ReceiptIcon />, color: T.blue },
    { title: 'Active Orders', value: data.activeOrders, icon: <LocalDiningIcon />, color: T.cyan },
    { title: 'Pending Orders', value: data.pendingOrders, icon: <KitchenIcon />, color: T.rose },
    { title: 'Occupied Tables', value: data.occupiedTableQty, icon: <TableRestaurantIcon />, color: T.purple },
    { title: 'Available Tables', value: data.availableTableQty, icon: <EventSeatIcon />, color: T.green },
    { title: 'Dine-In Today', value: data.totalDineIn, icon: <LocalDiningIcon />, color: T.amber },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      background: `linear-gradient(160deg,#EEF2FF 0%,#F8FAFF 50%,#EFF9FF 100%)`,
      width: '100%', maxWidth: '100vw', overflowX: 'hidden', boxSizing: 'border-box',
      p: { xs: '12px', sm: '16px', md: '22px' },
      pb: { xs: '140px', md: '100px' },
    }}>

      {/* #1: Restaurant dashboard date filter (no hotel dimension) */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', background: '#fff', border: '1px solid #e5e9f2', borderRadius: 12, padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>From</label>
          <input type='date' value={rFrom} onChange={(e) => setRFrom(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>To</label>
          <input type='date' value={rTo} onChange={(e) => setRTo(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1' }} />
        </div>
        <button onClick={applyRFilter} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Apply</button>
        <button onClick={() => { setRFrom(''); setRTo(''); setRQs(''); }} style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer' }}>Reset</button>
      </div>

      {/* ══════════════ HERO BANNER ══════════════ */}
      <Paper elevation={0} sx={{
        borderRadius: '20px', mb: '20px', overflow: 'hidden',
        background: 'linear-gradient(135deg,#020617 0%,#0f172a 45%,#1e3a8a 100%)',
        position: 'relative',
      }}>
        {/* Decorative blobs */}
        <Box sx={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle,rgba(96,165,250,0.18),transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: '30%', width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle,rgba(139,92,246,0.15),transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: '20%', left: -20, width: 100, height: 100, borderRadius: '50%', background: 'radial-gradient(circle,rgba(16,185,129,0.12),transparent 70%)', pointerEvents: 'none' }} />

        <Box sx={{ p: { xs: '18px 16px', md: '22px 26px' }, position: 'relative' }}>
          {/* Top row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '18px' }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.72rem' }, color: 'rgba(148,163,184,0.8)', fontWeight: 500, mb: '4px', letterSpacing: '0.2px' }}>
                {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                &nbsp;·&nbsp;
                {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
              <Typography sx={{ fontSize: { xs: '1.15rem', md: '1.5rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, letterSpacing: '-0.4px' }}>
                {greeting.emoji}&nbsp;{greeting.text},{' '}
                <span style={{ color: '#60a5fa' }}>{userName}</span>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: '5px',
                bgcolor: 'rgba(16,185,129,0.15)', border: '1px solid rgba(52,211,153,0.3)',
                borderRadius: '20px', px: '10px', py: '4px',
              }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: '50%', bgcolor: '#34d399',
                  animation: 'hpulse 2s infinite',
                  '@keyframes hpulse': { '0%,100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.5, transform: 'scale(0.85)' } },
                }} />
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#34d399', letterSpacing: '0.3px' }}>LIVE</Typography>
              </Box>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: '4px',
                bgcolor: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)',
                borderRadius: '20px', px: '10px', py: '4px',
              }}>
                <StarIcon sx={{ fontSize: 11, color: '#fbbf24' }} />
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#fbbf24' }}>{data.avgRating} Rating</Typography>
              </Box>
            </Box>
          </Box>

          {/* Hero stats */}
          <Grid container spacing={{ xs: 1, md: 1.5 }}>
            {heroStats.map((s, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{
                  borderRadius: '14px', p: { xs: '10px 12px', md: '12px 16px' },
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(12px)', transition: 'background 0.2s',
                  '&:hover': { background: 'rgba(255,255,255,0.09)' },
                }}>
                  <Typography sx={{ fontSize: '0.55rem', color: 'rgba(148,163,184,0.75)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', mb: '5px' }}>
                    {s.icon}&nbsp;{s.label}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 800, color: s.color, lineHeight: 1, letterSpacing: '-0.3px' }}>
                    {s.isAmt && <span style={{ fontSize: '0.58em', opacity: 0.65, marginRight: 2 }}>{Companycurrency}</span>}
                    <CountUp end={s.value || 0} separator="," decimals={s.decimals || 0} suffix={s.suffix || ''} duration={1.5} />
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {loading && (
          <LinearProgress sx={{
            height: 2, bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#60a5fa,#34d399)' },
          }} />
        )}
      </Paper>

      {/* ══════════════ REVENUE KPI CARDS ══════════════ */}
      <SectionLabel label="Revenue Summary" color={T.blue} />
      <Grid container spacing={1.5} sx={{ mb: '20px', width: '100%', mx: 0 }}>
        {kpiCards.map((item, i) => (
          <Grid item xs={6} sm={4} md={true} key={i} sx={{ minWidth: 0 }}>
            <KpiCard {...item} />
          </Grid>
        ))}
      </Grid>

      {/* ══════════════ TODAY'S PERFORMANCE TILES ══════════════ */}
      <SectionLabel
        label="Today's Performance"
        color={T.green}
        action={
          <Typography sx={{ fontSize: '0.58rem', color: T.textMut, fontWeight: 500 }}>
            {now.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>
        }
      />
      <Grid container spacing={1.5} sx={{ mb: '20px', width: '100%', mx: 0 }}>
        {perfTiles.map((item, i) => (
          <Grid item xs={6} sm={3} key={i} sx={{ minWidth: 0 }}>
            <MetricTile {...item} />
          </Grid>
        ))}
      </Grid>

      {/* ══════════════ ANALYTICS ROW ══════════════ */}
      <SectionLabel label="Analytics & Insights" color={T.amber} />
      <Grid container spacing={1.5} sx={{ width: '100%', mx: 0 }}>

        {/* Dine-In vs Takeaway */}
        <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
          <Paper elevation={0} sx={{
            borderRadius: '16px', background: T.card, border: `1px solid ${T.border}`,
            boxShadow: shadow.card, p: '18px', height: '100%',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px' }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: T.textSec, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                Dine-In vs Takeaway
              </Typography>
              <Box sx={{ display: 'flex', gap: '10px' }}>
                {[{ c: T.green, l: 'Dine' }, { c: T.rose, l: 'Take' }].map((d) => (
                  <Box key={d.l} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: d.c }} />
                    <Typography sx={{ fontSize: '0.57rem', color: T.textMut, fontWeight: 500 }}>{d.l}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { name: 'Dine-In', value: data.totalDineIn },
                    { name: 'Takeaway', value: data.totalTakeaway },
                  ]}
                  margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="bar-in" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.green} />
                      <stop offset="100%" stopColor={alpha(T.green, 0.4)} />
                    </linearGradient>
                    <linearGradient id="bar-out" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.rose} />
                      <stop offset="100%" stopColor={alpha(T.rose, 0.4)} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: T.textMut }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: T.textMut }} axisLine={false} tickLine={false} />
                  <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: alpha(T.blue, 0.04) }} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={56}>
                    <Cell fill="url(#bar-in)" />
                    <Cell fill="url(#bar-out)" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Revenue Breakdown */}
        <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
          <Paper elevation={0} sx={{
            borderRadius: '16px', background: T.card, border: `1px solid ${T.border}`,
            boxShadow: shadow.card, p: '18px', height: '100%',
          }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: T.textSec, letterSpacing: '0.6px', textTransform: 'uppercase', mb: '14px' }}>
              Revenue Breakdown
            </Typography>
            <Box sx={{ height: 185 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData} margin={{ top: 4, right: 4, left: -20, bottom: 34 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="name" angle={-30} textAnchor="end" tick={{ fontSize: 8, fill: T.textMut }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 8, fill: T.textMut }} axisLine={false} tickLine={false} />
                  <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: alpha(T.blue, 0.04) }} />
                  <Bar dataKey="amt" radius={[6, 6, 0, 0]} maxBarSize={40}>
                    {revenueData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Right Stats Column */}
        <Grid item xs={12} sm={12} md={4} sx={{ minWidth: 0 }}>
          <Grid container spacing={1.5} sx={{ height: '100%' }}>

            {/* Table Occupancy Ring Card */}
            <Grid item xs={12} sm={4} md={12}>
              <Paper elevation={0} sx={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#1A56DB 100%)',
                boxShadow: `0 8px 32px ${alpha(T.blue, 0.3)}`,
                p: '18px', position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: -24, right: -24, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(96,165,250,0.12)', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: '40%', width: 60, height: 60, borderRadius: '50%', bgcolor: 'rgba(139,92,246,0.15)', pointerEvents: 'none' }} />
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(148,163,184,0.8)', letterSpacing: '0.6px', textTransform: 'uppercase', mb: '12px' }}>
                  🍽️ Table Occupancy
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <OccupancyRing pct={occPct} size={84} />
                  <Box>
                    <Typography sx={{ fontSize: '2.2rem', fontWeight: 900, color: '#fff', lineHeight: 1, letterSpacing: '-1px' }}>
                      <CountUp end={occPct} decimals={1} suffix="%" duration={1.6} />
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(148,163,184,0.7)', mt: '3px' }}>
                      {data.occupiedTableQty} of {data.tableCount} tables
                    </Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: 'rgba(148,163,184,0.55)', mt: '2px' }}>
                      Reservations today: {data.reservationsToday}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Available Tables */}
            <Grid item xs={6} sm={4} md={12}>
              <StatMini
                label="Available Tables" value={data.availableTableQty} color={T.green}
                icon={<EventSeatIcon />} pct={availablePct}
                pctLabel={`${availablePct.toFixed(0)}% of ${data.tableCount} total tables`}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* ══════════════ TOP SELLING ITEMS ══════════════ */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: '16px',
          background: '#fff',
          border: `1px solid ${alpha(T.blue, 0.12)}`,
          boxShadow: '0 4px 20px rgba(15,23,42,0.05)',
          p: { xs: 1.8, md: 2.4 },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.6,
          }}>
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 800,
              color: T.textPri,
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
            }}>
            🔥 Top Selling Items
          </Typography>
          {/* Date filter: Today / Week / Month */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              bgcolor: alpha(T.blue, 0.07),
              p: 0.4,
              borderRadius: 2,
            }}>
            {[
              ['today', 'Today'],
              ['week', 'Weekly'],
              ['month', 'Monthly'],
            ].map(([val, label]) => (
              <Box
                key={val}
                onClick={() => setTopPeriod(val)}
                sx={{
                  px: 1.4,
                  py: 0.5,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  userSelect: 'none',
                  transition: 'all 0.15s',
                  bgcolor: topPeriod === val ? T.blue : 'transparent',
                  color: topPeriod === val ? '#fff' : T.textSec,
                }}>
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        {topItems.length === 0 ? (
          <Typography
            sx={{ fontSize: '0.8rem', color: T.textSec, py: 2, textAlign: 'center' }}>
            No orders in this period yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {topItems.map((it) => {
              const rankColor =
                it.rank === 1
                  ? '#f59e0b'
                  : it.rank === 2
                    ? '#94a3b8'
                    : it.rank === 3
                      ? '#b45309'
                      : alpha(T.blue, 0.55);
              return (
                <Box
                  key={it.itemId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    p: 1,
                    borderRadius: 2,
                    bgcolor: alpha(T.blue, 0.03),
                  }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: rankColor,
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    {it.rank}
                  </Box>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: T.textPri,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {it.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: T.textSec,
                      flexShrink: 0,
                    }}>
                    {it.orderCount} sold
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 800,
                      color: T.green,
                      minWidth: 70,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}>
                    {Companycurrency}
                    {Number(it.revenue || 0).toLocaleString()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* ══════════════ FLOATING BUTTONS ══════════════ */}
      <Box sx={{
        position: 'fixed', bottom: { xs: 14, md: 26 }, right: { xs: 14, md: 26 },
        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'flex-end', gap: '10px', zIndex: 1300,
      }}>
        <Tooltip title="View Reservations" placement="left" arrow>
          <Fab
            size="small"
            onClick={() => navigate('/admin/Reservations')}
            sx={{
              width: 42, height: 42, background: T.card, color: T.blue,
              border: `1.5px solid ${alpha(T.blue, 0.2)}`,
              boxShadow: `0 4px 16px ${alpha(T.blue, 0.2)}`,
              '&:hover': { transform: 'scale(1.1)', background: '#EEF2FF', boxShadow: `0 6px 24px ${alpha(T.blue, 0.3)}` },
              transition: 'all 0.2s',
            }}>
            <CalendarTodayIcon sx={{ fontSize: 19 }} />
          </Fab>
        </Tooltip>

        <Fab
          variant="extended"
          onClick={() => navigate('/admin/CreateOrder')}
          sx={{
            height: 42, px: '18px', borderRadius: '12px',
            background: `linear-gradient(135deg,${T.blue},${T.blueL})`,
            color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'none',
            boxShadow: `0 4px 18px ${alpha(T.blue, 0.4)}`, gap: '6px', whiteSpace: 'nowrap', letterSpacing: '0.1px',
            '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 8px 28px ${alpha(T.blue, 0.5)}` },
            transition: 'all 0.2s',
          }}>
          <AddIcon sx={{ fontSize: 17 }} /> New Order
        </Fab>

        <Fab
          variant="extended"
          onClick={() => navigate('/admin/Reservations?new=1')}
          sx={{
            height: 42, px: '18px', borderRadius: '12px',
            background: `linear-gradient(135deg,${T.green},#34d399)`,
            color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'none',
            boxShadow: `0 4px 18px ${alpha(T.green, 0.4)}`, gap: '6px', whiteSpace: 'nowrap', letterSpacing: '0.1px',
            '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 8px 28px ${alpha(T.green, 0.5)}` },
            transition: 'all 0.2s',
          }}>
          <AddIcon sx={{ fontSize: 17 }} /> Reserve Table
        </Fab>
      </Box>
    </Box>
  );
}